import { batch, createSignal, type Accessor } from 'solid-js';
import type { DrawingTool } from '@trimble/geospatial';

export interface CadEditingUiState {
  readonly cadEditingMode: Accessor<boolean>;
  readonly desiredDrawingTool: Accessor<DrawingTool | undefined>;
  readonly allowedDrawingTools: Accessor<readonly DrawingTool[]>;
  setCadEditingMode(enabled: boolean): void;
  setDesiredDrawingTool(tool: DrawingTool | undefined): void;
  onSelectedLayerChanged(): void;
  reset(): void;
}

export function createCadEditingUiState(
  getMappedTools: Accessor<readonly DrawingTool[]>
): CadEditingUiState {
  const [cadEditingMode, setCadEditingModeSignal] = createSignal(false);
  const [desiredDrawingTool, setDesiredDrawingToolSignal] = createSignal<
    DrawingTool | undefined
  >(undefined);

  const setCadEditingMode = (enabled: boolean): void => {
    batch(() => {
      setCadEditingModeSignal(enabled);
      if (!enabled) {
        setDesiredDrawingToolSignal(undefined);
      }
    });
  };

  const setDesiredDrawingTool = (tool: DrawingTool | undefined): void => {
    if (tool === undefined) {
      setDesiredDrawingToolSignal(undefined);
      return;
    }
    if (!getMappedTools().includes(tool)) {
      return;
    }
    batch(() => {
      setDesiredDrawingToolSignal(tool);
      setCadEditingModeSignal(true);
    });
  };

  const onSelectedLayerChanged = (): void => {
    const current = desiredDrawingTool();
    if (current !== undefined && !getMappedTools().includes(current)) {
      setDesiredDrawingToolSignal(undefined);
    }
  };

  const reset = (): void => {
    setCadEditingModeSignal(false);
    setDesiredDrawingToolSignal(undefined);
  };

  return {
    cadEditingMode,
    desiredDrawingTool,
    allowedDrawingTools: getMappedTools,
    setCadEditingMode,
    setDesiredDrawingTool,
    onSelectedLayerChanged,
    reset,
  };
}
